import { faBullhorn, faCalendar, faRocket } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { add, startOfDay } from "date-fns";
import { SubmitHandler, useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";
import { Button, Form, Segment } from "semantic-ui-react";
import { FileUsage } from "../../../../../../actions/coreUpload";
import { ControlledInput } from "../../../../../../components/ControlledForm";
import {
  AccessibleDropdownOption,
  ControlledAccessibleDropdown,
} from "../../../../../../components/global_components/form_components/accessible/AccessibleDropdown";
import { ControlledRichTextEditor } from "../../../../../../components/global_components/form_components/controlled-rich-text-editor/ControlledRichTextEditor";
import { richTextEditorEmptyDocumentJSONString } from "../../../../../../components/global_components/form_components/controlled-rich-text-editor/util/controlledRichTextEditorMarkdown";
import { useTimezone } from "../../../../../../hooks/useTimezone";
import useProducts from "../../../../../admin/employees/hooks/useProducts";
import useSite from "../../../../../permits/components/inspections/InspectionPanel/useSite";
import { useNotifySubscriptionGroups } from "../../../../hooks/useNotifySubscriptionGroups";
import { ChannelType } from "../enums/ChannelType";
import { useNotifyUserDepartments } from "../pages/notifications/hooks/useNotifyUserDepartments";
import { ControlledDatetimeToggle } from "./ControlledDatetimeToggle";
import { ControlledGeoTargetToggle } from "./ControlledGeoTargetToggle";
import { ControlledNotifyNotificationsChannels } from "./ControlledNotifyNotificationsChannels";
import { ControlledTextAreaToggle } from "./ControlledTextAreaToggle";
import { ControlledTwoWayCommunicationDropdown } from "./ControlledTwoWayCommunicationDropdown";
import { NotifyTwoWayCommunicationPromoBanner } from "./NotifyTwoWayCommunicationPromoBanner";
import { useNotifyNotificationInfo } from "../../../../hooks/useNotifyNotificationInfo";

export type NotifyNotificationsFormDataChannels = {
  active: ChannelType[];
  additionalData: {
    voiceForceSendAll: boolean;
  };
};

export type NotifyNotificationsFormData = {
  fromDepartment: AccessibleDropdownOption<number> | null;
  headline: string;
  tagline: string;
  body: string;
  channels: NotifyNotificationsFormDataChannels;
  classificationId: AccessibleDropdownOption<number> | null;
  sendAt: Date | null;
  archiveAt: Date | null;
  subscriptionGroups: AccessibleDropdownOption<number>[];
  voiceTranscription?: string | null;
  geoTargetFeatures: {
    featureIds: number[];
    restrictBoundary: boolean;
  } | null;
};

export const NotifyNotificationsFormDefaults: NotifyNotificationsFormData = {
  fromDepartment: null,
  headline: "",
  tagline: "",
  body: richTextEditorEmptyDocumentJSONString(),
  channels: {
    active: [],
    additionalData: {
      voiceForceSendAll: false,
    },
  },
  classificationId: null,
  sendAt: null,
  archiveAt: null,
  subscriptionGroups: [],
  voiceTranscription: "",
  geoTargetFeatures: null,
};

export const notifyNotificationsFormResolver = (values: NotifyNotificationsFormData) => {
  const errors: { field: keyof typeof values; type: string; message: string }[] = [];

  if (values.fromDepartment === null) {
    errors.push({
      field: "fromDepartment",
      type: "required",
      message: "From Sending Group is required",
    });
  }

  if (values.headline?.trim().length === 0) {
    errors.push({
      field: "headline",
      type: "required",
      message: "Headline is required",
    });
  }

  const hasVoiceChannel = values.channels.active.includes(ChannelType.Voice);
  if (hasVoiceChannel && !values.voiceTranscription?.trim()) {
    errors.push({
      field: "voiceTranscription",
      type: "required",
      message: "Voice transcription is required",
    });
  }

  if (values.subscriptionGroups.length === 0) {
    errors.push({
      field: "subscriptionGroups",
      type: "required",
      message: "At least one Subscription Group is required",
    });
  }

  const hasSendAt = !!values.sendAt;
  const sendAtIsInPast = hasSendAt ? values.sendAt! < new Date() : false;
  if (hasSendAt && sendAtIsInPast) {
    errors.push({
      field: "sendAt",
      type: "validate",
      message: "Post date must be in the future",
    });
  }

  const hasArchiveAt = !!values.archiveAt;
  const hasArchiveAtAndSendAt = hasArchiveAt && hasSendAt;
  const archiveAtIsInPast = hasArchiveAt ? values.archiveAt! < new Date() : false;
  const archiveAtIsBeforeSendAt = hasArchiveAtAndSendAt && values.archiveAt! < add(values.sendAt!, { days: 1 });
  if (hasArchiveAt && archiveAtIsInPast) {
    errors.push({
      field: "archiveAt",
      type: "validate",
      message: "Archive date must be in the future",
    });
  }
  if (hasArchiveAtAndSendAt && archiveAtIsBeforeSendAt) {
    errors.push({
      field: "archiveAt",
      type: "validate",
      message: "Archive date must be at least 1 day after post date",
    });
  }

  return {
    values,
    errors: errors.reduce((acc, error) => {
      acc[error.field] = { type: error.type, message: error.message };
      return acc;
    }, {} as any),
  };
};

export function NotifyNotificationsForm({
  onContinue,
  onSaveDraft,
  isSaveDraftLoading: isSaveDraftLoading,
  addMediaFile,
}: {
  onContinue: () => void;
  onSaveDraft: SubmitHandler<NotifyNotificationsFormData>;
  isSaveDraftLoading?: boolean;
  addMediaFile: (uuid: string) => void;
}) {
  const site = useSite();
  const { control, errors, handleSubmit, watch, setValue } = useFormContext<NotifyNotificationsFormData>();

  const {
    defaultTimezoneInfo: { timezone },
  } = useTimezone();

  const { departments, isLoading: isUserDepartmentsLoading } = useNotifyUserDepartments({ limitToUsersRoles: true });
  const { subscriptionGroups, isSubscriptionGroupsLoading } = useNotifySubscriptionGroups();

  const {
    channels,
    sendAt,
    geoTargetFeatures,
    subscriptionGroups: selectedSubscriptionGroups,
    voiceTranscription,
  } = watch(["channels", "sendAt", "geoTargetFeatures", "subscriptionGroups", "voiceTranscription"]);
  const hasVoiceChannel = channels?.active.includes(ChannelType.Voice);

  const setChannel = (channel: ChannelType, on: boolean) => {
    if (on) {
      setValue("channels", [...channels.active.filter((c) => c !== channel), channel]);
    } else {
      setValue(
        "channels",
        channels.active.filter((c) => c !== channel),
      );
    }
  };

  const { hasCrm, isLoading: isProductsQueryLoading } = useProducts();

  const { notificationInfo } = useNotifyNotificationInfo({
    body: {
      channels: channels.active,
      geoTargeting: geoTargetFeatures
        ? {
            featureIds: geoTargetFeatures.featureIds,
            restrictToGeoTargetBoundary: geoTargetFeatures.restrictBoundary,
          }
        : null,
      subscriptionGroupIds: selectedSubscriptionGroups.map((group) => group.value),
      voiceForceSendAll: channels.additionalData.voiceForceSendAll,
      voiceCharacterCount: voiceTranscription ? voiceTranscription.length : 0,
    },
  });
  const smsTotalMessages = notificationInfo?.billing.channels.sms.totalMessages;
  const voiceTotalMessages = notificationInfo?.billing.channels.voice.totalMessages;

  return (
    <Form className='tw-flex tw-w-full tw-flex-col tw-gap-9'>
      <Segment className='tw-m-0 tw-bg-white tw-p-2 tw-shadow-sm'>
        <div className='tw-flex tw-items-center tw-gap-6 tw-rounded-sm tw-bg-white-background tw-px-8 tw-py-6'>
          <FontAwesomeIcon icon={faBullhorn} className='tw-h-6 tw-w-6 tw-text-text-secondary' />
          <h2 className='tw-m-0 tw-text-2xl'>Add Your Message</h2>
        </div>
        <div className='tw-mx-8 tw-my-6'>
          <Form.Field required>
            <ControlledAccessibleDropdown
              control={control}
              name='fromDepartment'
              label='From Sending Group'
              options={departments?.map((department) => ({
                value: department.id,
                label: department.name,
              }))}
              isLoading={isUserDepartmentsLoading}
              errors={errors}
            />
          </Form.Field>
          <Form.Field required>
            <ControlledInput control={control} name='headline' label='Headline' errors={errors} />
          </Form.Field>
          <Form.Field>
            <ControlledInput control={control} name='tagline' label='Subheading' errors={errors} />
          </Form.Field>
          <Form.Field>
            <ControlledRichTextEditor
              control={control}
              name='body'
              label='Body'
              errors={errors}
              format='json'
              mediaFileUsage={FileUsage.NotifyEditorMedia}
              addMediaFile={addMediaFile}
            />
          </Form.Field>
          <Form.Field error={!!errors.voiceTranscription} required={hasVoiceChannel}>
            <div className='tw-space-y-2'>
              <ControlledTextAreaToggle
                control={control}
                name='voiceTranscription'
                label='Add Voice Transcription'
                placeholder='The voice message transcription, only used if voice channel is selected'
                rows={3}
                errors={errors}
                overrideToggle={hasVoiceChannel}
                onToggle={(show) => {
                  setChannel(ChannelType.Voice, show);
                }}
              />
            </div>
          </Form.Field>
          <Form.Field>
            <ControlledTwoWayCommunicationDropdown
              control={control}
              name='classificationId'
              label='Enable Two-Way Communication'
              disabled={!hasCrm}
            />

            <NotifyTwoWayCommunicationPromoBanner hasCrm={hasCrm} isProductsQueryLoading={isProductsQueryLoading} />
          </Form.Field>
        </div>
      </Segment>
      <Segment className='tw-m-0 tw-bg-white tw-p-2 tw-shadow-sm'>
        <div className='tw-flex tw-gap-6 tw-rounded-sm tw-bg-white-background tw-px-8 tw-py-6'>
          <FontAwesomeIcon icon={faRocket} className='tw-mt-[5px] tw-h-6 tw-w-6 tw-text-text-secondary' />
          <div>
            <h2 className='tw-m-0 tw-text-2xl'>Audience Controls</h2>
            <p className='tw-m-0 tw-text-text-secondary'>
              Narrow down your notifications to specific channels, groups and areas based on your needs.
            </p>
          </div>
        </div>
        <div className='tw-mx-8 tw-my-6'>
          <Form.Field error={!!errors.subscriptionGroups} required>
            <ControlledAccessibleDropdown
              control={control}
              name='subscriptionGroups'
              options={
                subscriptionGroups?.map((group) => ({
                  value: group.id,
                  label: group.name,
                })) || []
              }
              label='Subscription Groups'
              subLabel='Narrow down to specific subscription groups or private groups.'
              multiple
              clearable
              isLoading={isSubscriptionGroupsLoading}
              errors={errors}
              selectAllOption
              selectAllLabel='Select All Groups'
            />
          </Form.Field>
          <Form.Field>
            <ControlledGeoTargetToggle control={control} name='geoTargetFeatures' label='Geo-Target' />
          </Form.Field>
          <h3 className='tw-m-0 tw-text-base'>Add Channels</h3>
          <p className='tw-mb-4 tw-mt-0 tw-text-sm tw-text-text-hint'>
            Sent messages will also be available in the mobile app and on the web.
          </p>
          <ControlledNotifyNotificationsChannels
            control={control}
            name='channels'
            smsEstimatedCount={smsTotalMessages}
            voiceEstimatedCount={voiceTotalMessages}
          />
        </div>
      </Segment>
      <Segment className='tw-m-0 tw-bg-white tw-p-2 tw-shadow-sm'>
        <div className='tw-flex tw-items-center tw-gap-6 tw-rounded-sm tw-bg-white-background tw-px-8 tw-py-6'>
          <FontAwesomeIcon icon={faCalendar} className='tw-h-6 tw-w-6 tw-text-text-secondary' />
          <h2 className='tw-m-0 tw-text-2xl'>Schedule</h2>
        </div>
        <div className='tw-mx-8 tw-my-6'>
          <Form.Field error={!!errors.sendAt}>
            <ControlledDatetimeToggle control={control} name='sendAt' label='Schedule To Post' errors={errors} />
          </Form.Field>
          <Form.Field error={!!errors.archiveAt}>
            <ControlledDatetimeToggle
              control={control}
              name='archiveAt'
              label='Set Archive Date'
              isDateUnavailable={(dateValue) => {
                const date = dateValue.toDate(timezone);
                const dateCutoff = sendAt ? new Date(sendAt) : new Date();
                return date < startOfDay(add(dateCutoff, { days: 1 }));
              }}
              errors={errors}
            />
          </Form.Field>
        </div>
      </Segment>
      <div className='tw-flex tw-items-center tw-justify-between tw-gap-3 tw-overflow-x-auto'>
        <Button
          className='tw-m-0 tw-whitespace-nowrap'
          as={Link}
          to={`/${site}/notify/notifications`}
          secondary
          type='button'
          disabled={isSaveDraftLoading}
        >
          Return to List
        </Button>
        <div className='tw-flex tw-gap-3'>
          <Button
            className='tw-m-0 tw-whitespace-nowrap'
            secondary
            type='submit'
            disabled={isSaveDraftLoading}
            loading={isSaveDraftLoading}
            onClick={handleSubmit(onSaveDraft)}
          >
            Save to Drafts
          </Button>
          <Button
            className='tw-m-0 tw-min-w-48 tw-whitespace-nowrap'
            primary
            type='button'
            onClick={handleSubmit(onContinue)}
          >
            Continue
          </Button>
        </div>
      </div>
    </Form>
  );
}
